/**
 * Inventory Page.
 *
 * Enterprise-grade inventory management with:
 * - List/grid view toggle with localStorage persistence
 * - Thumbnails from asset images
 * - Advanced filter panel (status, category, price, date, hold)
 * - Sort options (date, name, price, status)
 * - Active filter pills with clear actions
 * - Search with result count and clear button
 */

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Plus, List, LayoutGrid, Upload, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import CalcTrigger from "@/components/ui/CalcTrigger";
import { AuthImage } from "@/components/ui/PhotoGallery";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import BulkActionBar from "@/components/ui/BulkActionBar";
import BulkResultPanel from "@/components/BulkResultPanel";
import {
  EMPTY_SELECTION,
  isRowSelected,
  selectAllMatching,
  selectionSize,
  toBulkSelection,
  toggleRow,
  useBulkRun,
  type BulkOutcome,
  type SelectionState,
} from "@/features/bulk/api";
import { useAssetSearch } from "@/features/inventory/api";
import { buildFilterTree } from "@/features/inventory/filterTree";

import AssetStatusBadge from "@/features/inventory/components/AssetStatusBadge";
import AssetGridCard from "@/features/inventory/components/AssetGridCard";
import InventoryFilters from "@/features/inventory/components/InventoryFilters";
import {
  EMPTY_FILTERS,
  type InventoryFilterState,
} from "@/features/inventory/filterTree";
import Button from "@/components/ui/Button";
import ExportButton from "@/components/ui/ExportButton";
import Card from "@/components/ui/Card";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

type ViewMode = "list" | "grid";

const VIEW_PREF_KEY = "inventory_view_mode";

function InventoryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  // Selection is a query, not a list (FS-030). A page renders 25 of 593
  // machines, so "select all" cannot mean "send 593 ids" — it means "everything
  // matching this filter as of now, less what I untick". The server resolves
  // it, with the moment and the count pinned so it cannot reach a machine that
  // arrived while this dialog was open.
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const toggleSelect = (id: string) =>
    setSelection((prev) => toggleRow(prev, id));
  const clearSelection = () => setSelection(EMPTY_SELECTION);
  const selectedCount = selectionSize(selection);
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_PREF_KEY);
    return saved === "grid" ? "grid" : "list";
  });

  const [filters, setFilters] = useState<InventoryFilterState>(EMPTY_FILTERS);

  // Persist view preference.
  useEffect(() => {
    localStorage.setItem(VIEW_PREF_KEY, viewMode);
  }, [viewMode]);

  // Reset page when filters change.
  const handleFiltersChange = (newFilters: InventoryFilterState) => {
    setFilters(newFilters);
    setPage(1);
  };

  // Everything the panel can express compiles to one filter tree and goes to
  // one endpoint (FS-029). The old flat parameters still work on `GET /assets`
  // — they build the same tree server-side — but there is no reason for this
  // page to use two shapes.
  const { data, isLoading } = useAssetSearch({
    page,
    page_size: viewMode === "grid" ? 24 : 25,
    search: filters.search || undefined,
    sort_by: filters.sortBy || undefined,
    sort_dir: filters.sortDir,
    show_archived: showArchived,
    filters: buildFilterTree(filters, filters.advanced) ?? undefined,
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  // One request, and the server reports every row. The loop this replaces fired
  // one `DELETE ?force=true` per selected machine — a cascade delete each, with
  // no preview — and discarded every failure behind a count.
  const bulkRun = useBulkRun();
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);

  const runBulk = (operation: string, params: Record<string, unknown> = {}) => {
    bulkRun.mutate(
      {
        operation,
        selection: toBulkSelection(selection, {
          search: filters.search || undefined,
          show_archived: showArchived,
          filters: buildFilterTree(filters, filters.advanced) ?? undefined,
        }),
        params,
      },
      {
        onSuccess: (outcome) => {
          qc.invalidateQueries({ queryKey: ["assets"] });
          clearSelection();
          setBulkOutcome(outcome.failed > 0 ? outcome : null);
          if (outcome.failed === 0) toast.success(outcome.message);
          else if (outcome.succeeded === 0) toast.error(outcome.message);
          else toast.warning(outcome.message);
        },
        onError: (error: unknown) => {
          // A refused selection lands here — the list changed under the user,
          // or it is too large. The server's sentence says which, and says what
          // to do, so it is shown rather than replaced with "Delete failed".
          const detail =
            (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? "That could not be done.";
          toast.error(detail);
        },
      },
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Inventory
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage, search, and organize your asset inventory.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-[var(--radius-md)] border border-[var(--border-default)] overflow-hidden">
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-[var(--color-interactive-primary)] text-white"
                  : "bg-[var(--surface-base)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
              }`}
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "grid"
                  ? "bg-[var(--color-interactive-primary)] text-white"
                  : "bg-[var(--surface-base)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Grid
            </button>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setPage(1);
              }}
              className="rounded border-[var(--border-default)]"
            />
            Archived
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/admin/integrations?tab=import")}
          >
            <Upload className="h-3.5 w-3.5" />
            Import
          </Button>
          <ExportButton
            url="/api/v1/data/export/assets"
            fallbackFilename="assets"
          />
          <Button onClick={() => navigate("/inventory/new")}>
            <Plus className="h-4 w-4" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Filters */}
      <InventoryFilters
        filters={filters}
        onChange={handleFiltersChange}
        totalResults={data?.total ?? 0}
      />

      {/* Asset List / Grid */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : data && data.items.length > 0 ? (
        <>
          {viewMode === "grid" ? (
            /* Grid View */
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 stagger-children">
              {data.items.map((asset) => (
                <AssetGridCard key={asset.id} asset={asset} />
              ))}
            </div>
          ) : (
            /* List View */
            <div className="space-y-2 stagger-children">
              {data.items.map((asset) => {
                const daysInInventory = Math.floor(
                  (Date.now() - new Date(asset.created_at).getTime()) /
                    86400000,
                );
                const firstImage =
                  asset.images && asset.images.length > 0
                    ? asset.images[0]
                    : null;

                return (
                  <Card
                    key={asset.id}
                    padding="none"
                    className="cursor-pointer hover-lift"
                    onClick={() => navigate(`/inventory/${asset.id}`)}
                  >
                    <div className="flex items-stretch">
                      <div
                        className="flex items-center pl-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isRowSelected(selection, asset.id)}
                          onChange={() => toggleSelect(asset.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="shrink-0 rounded border-[var(--border-default)]"
                        />
                      </div>
                      {/* Thumbnail */}
                      <div className="hidden sm:flex shrink-0 w-28 items-center justify-center border-r border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-1.5">
                        {firstImage ? (
                          <AuthImage
                            attachmentId={firstImage as string}
                            alt=""
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Package className="h-6 w-6 text-[var(--text-tertiary)]/30" />
                        )}
                      </div>

                      {/* Left: Primary info */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                                {asset.name}
                              </h3>
                              <AssetStatusBadge status={asset.status} />
                              {asset.is_on_hold && (
                                <Badge variant="warning">On hold</Badge>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-xs text-[var(--text-tertiary)]">
                              <span className="font-mono text-brand-600">
                                {asset.sku}
                              </span>
                              <span className="capitalize">
                                {asset.category}
                              </span>
                              {asset.year && <span>{asset.year}</span>}
                              {asset.make && <span>{asset.make}</span>}
                              {asset.model && <span>{asset.model}</span>}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                          {asset.serial_number && (
                            <span>
                              S/N:{" "}
                              <span className="font-mono">
                                {asset.serial_number}
                              </span>
                            </span>
                          )}
                          {asset.vin && (
                            <span>
                              VIN:{" "}
                              <span className="font-mono">{asset.vin}</span>
                            </span>
                          )}
                          {asset.lot_number && (
                            <span>
                              Lot:{" "}
                              <span className="font-mono">
                                {asset.lot_number}
                              </span>
                            </span>
                          )}
                          {asset.location && <span>{asset.location}</span>}
                          <span>{daysInInventory}d in inventory</span>
                        </div>
                      </div>

                      {/* Right: Financial summary */}
                      <div className="hidden sm:flex shrink-0 items-center border-l border-[var(--color-border-light)] bg-[var(--color-surface-tertiary)]">
                        <div className="w-[110px] text-right px-3 py-4">
                          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                            Purchase
                          </p>
                          <p className="text-sm tabular-nums font-medium text-[var(--text-secondary)] mt-0.5">
                            {formatCurrency(asset.purchase_price)}
                          </p>
                        </div>
                        <div className="w-[110px] text-right px-3 py-4">
                          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                            Landed
                          </p>
                          <p className="text-sm tabular-nums font-medium text-[var(--text-secondary)] mt-0.5">
                            {formatCurrency(asset.landed_cost)}
                          </p>
                        </div>
                        <div className="w-[120px] text-right px-3 py-4 pr-5">
                          <div className="flex items-center justify-end gap-1">
                            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                              Total COGS
                            </p>
                            <CalcTrigger
                              mode="margin"
                              prefill={{ cost: String(asset.total_cogs) }}
                              source={asset.sku}
                            />
                          </div>
                          <p className="text-sm tabular-nums font-bold text-[var(--text-primary)] mt-0.5">
                            {formatCurrency(asset.total_cogs)}
                          </p>
                        </div>
                      </div>
                      <div
                        className="flex items-center gap-1 shrink-0 pr-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => setDeleteTarget(asset.id)}
                          className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-[var(--text-secondary)]">
                Page {data.page} of {data.pages} ({data.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= data.pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Package />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No assets found
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {filters.search
                ? "Try a different search term or adjust your filters."
                : "Get started by adding your first asset."}
            </p>
            {!filters.search && (
              <Link to="/inventory/new">
                <Button className="mt-4">
                  <Plus className="h-4 w-4" />
                  Add Asset
                </Button>
              </Link>
            )}
          </div>
        </Card>
      )}

      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/assets"
        entityId={deleteTarget || ""}
        queryKey="assets"
      />

      {selectedCount > 0 &&
        selection.mode === "ids" &&
        (data?.total ?? 0) > selectedCount && (
          <Card padding="sm">
            <p className="text-sm text-[var(--text-secondary)]">
              {selectedCount} selected on this page.{" "}
              <button
                type="button"
                className="font-semibold text-[var(--color-interactive-primary)] underline-offset-2 hover:underline"
                onClick={() =>
                  setSelection(selectAllMatching(data?.total ?? 0))
                }
              >
                Select all {data?.total} matching this filter
              </button>
            </p>
          </Card>
        )}

      {selection.mode === "all" && (
        <Card padding="sm">
          <p className="text-sm text-[var(--text-secondary)]">
            All {selection.total} machines matching this filter are selected
            {selection.excluded.size > 0
              ? `, less the ${selection.excluded.size} you unticked`
              : ""}
            .{" "}
            <button
              type="button"
              className="font-semibold text-[var(--color-interactive-primary)] underline-offset-2 hover:underline"
              onClick={clearSelection}
            >
              Clear
            </button>
          </p>
        </Card>
      )}

      <BulkResultPanel
        outcome={bulkOutcome}
        onClose={() => setBulkOutcome(null)}
        noun="machines"
      />

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            label: "Place on Hold",
            onClick: () =>
              runBulk("asset.hold", { reason: "Bulk hold from inventory" }),
            isLoading: bulkRun.isPending,
          },
          {
            label: "Release Hold",
            onClick: () => runBulk("asset.release_hold"),
            isLoading: bulkRun.isPending,
          },
          {
            label: "Delete Selected",
            onClick: () => runBulk("asset.delete", { cascade: true }),
            variant: "danger",
            isLoading: bulkRun.isPending,
          },
        ]}
      />
    </div>
  );
}

export default InventoryPage;
